import React, { useEffect, useMemo, useState } from "react";
import { useIntl, FormattedMessage } from "react-intl";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { AppDispatch, RootState } from "../../../../store";

import {
    fetchEmployeeRequests,
    fetchRequestsColumns,
    setSelectedIds as setSelectedIdsAction,
    setSearchString as setSearchStringAction,
    setPageNumber as setPageNumberAction,
    setLimitNumber as setLimitNumberAction,
    setSortData as setSortDataAction,
    setFilterData as setFilterDataAction,
    setError as setErrorAction,
    // setRequestsType as setRequestsTypeAction,
    fetchRequestStatuses,
    setValidationErrors,
    getRequestStages,
} from "../../../../reducers/administration/requestSlice";

import useFilters from "../../../common/hooks/useFilters";
import CommonTable from "../../../common/table";
import useReduxTable from "../../../common/hooks/useReduxTable";
import { ProgressDialog, ErrorsDialog } from "../../../common/dialogs";
import { createRowCellContentInner } from "../../../common/table";
import { SuccessIcon, TimeIcon, ErrorIcon } from "../../../common/icons";
import { Box, Typography, IconButton, Tooltip, MenuItem, Popover } from "@mui/material";
//TODO: переделать иконки
import LanguageIcon from "@mui/icons-material/Language";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import CheckIcon from "@mui/icons-material/Check";
import { Column } from "../../../../types/common/table/table-types";
import { GetRequestStatusesResponse, ChangeRequest } from "@/types/brm";
import { ResponseError } from "../../../../types/common/responses/responses-types";
import { useDispatch } from "react-redux";
import ThreeDotsButton from "../../../common/buttons/three-dots-button";
import CreateRequestDialog from "../../administration/requests-list/create-request-dialog";

type StatusMapping = {
    [key: string]: {
        icon?: JSX.Element;
        text: string;
    };
};

const statusMapping: StatusMapping = {
    ROLE_DEFINITION: {
        icon: <TimeIcon color="primary" />,
        text: "На согласовании",
    },
    rejected: {
        icon: <ErrorIcon color="error" />,
        text: "Отклонено",
    },
    approved: {
        icon: <SuccessIcon color="success" />,
        text: "Утверждено",
    },
    completed: {
        icon: <CheckIcon color="success" />,
        text: "Завершено",
    },
    TEST_APPROVEMENT: {
        icon: <TimeIcon color="primary" />,
        text: "На согласовании",
    },
    total: {
        text: "Всего",
    },
};

type FilterStatusPopoverProps = {
    setAnchorEl: (element: HTMLButtonElement | null) => void;
    anchorEl: HTMLButtonElement | null;
    statuses: GetRequestStatusesResponse | null;
};

const FilterStatusPopover: React.FC<FilterStatusPopoverProps> = ({ setAnchorEl, anchorEl, statuses }) => {
    const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
        setAnchorEl(event.currentTarget);
    };

    const handleClose = () => {
        setAnchorEl(null);
    };

    const open = Boolean(anchorEl);

    return (
        <Box position="relative">
            <IconButton onClick={handleClick}>
                <MoreVertIcon />
            </IconButton>
            <Popover
                open={open}
                anchorEl={anchorEl}
                onClose={handleClose}
                anchorOrigin={{
                    vertical: "bottom",
                    horizontal: "left",
                }}
            >
                <MenuItem disabled={true}>
                    <Box display="flex" justifyContent="space-between" alignItems="center" width="100%">
                        <Typography noWrap>{statusMapping["total"].text}</Typography>
                        <Typography ml={2}>{statuses?.total}</Typography>
                    </Box>
                </MenuItem>
                {["inProgress", "rejected", "approved", "completed"].map((key) => (
                    <MenuItem key={key}>
                        <Box display="flex" justifyContent="space-between" alignItems="center" width="100%">
                            <Typography noWrap>{statusMapping[key].text}</Typography>
                            <Typography ml={2}>{statuses?.[key]}</Typography>
                        </Box>
                    </MenuItem>
                ))}
            </Popover>
        </Box>
    );
};

const getCellValue = (row: ChangeRequest, column: Column) => {
    switch (column.id) {
        case "creationDate":
            return row.createdAt;

        default:
            return row[column.id];
    }
};

const TaskList: React.FC = () => {
    const intl = useIntl();
    const navigate = useNavigate();
    const [anchorEl, setAnchorEl] = React.useState<HTMLButtonElement | null>(null);
    const createFilterStatusPopover = (anchor: HTMLButtonElement, columnId: string): boolean => {
        if (columnId !== "status") return false;
        setAnchorEl(anchor);
        return true;
    };
    const dispatch = useDispatch<AppDispatch>();

    useEffect(() => {
        dispatch(fetchRequestStatuses());
        dispatch(getRequestStages());
    }, [dispatch]);

    const statuses = useSelector((state: RootState) => state.administration.requests.requestStatuses);
    const stages = useSelector((state: RootState) => state.administration.requests.requestStages);

    const stageMap =
        stages &&
        stages.reduce((accumulator, item) => {
            accumulator[item.stage] = item;

            return accumulator;
        }, {});

    const createRowCellContent = (
        row: ChangeRequest,
        column: Column,
        selected: boolean,
        onSelect: (id: string) => void,
        getCellValue: (row: ChangeRequest, column: Column) => string,
        stageMap: { [key: string]: { text: string } }
    ): JSX.Element => {
        switch (column.id) {
            case "hasRisks": {
                return <Typography>{row[column.id] ? intl.formatMessage({ id: "common.yes-text" }) : intl.formatMessage({ id: "common.no-text" })}</Typography>;
            }

            case "currentStage": {
                const stage = row[column.id];

                return <Typography>{(stage && stageMap[stage] && stageMap[stage].text) || ""}</Typography>;
            }

            case "status": {
                const status = row[column.id];
                const statusInfo = statusMapping[status];
                const menuItems = [
                    {
                        label: <FormattedMessage id="requests-list-page.toolbar.popover.open" />,
                        onClick: () => alert("Открыть"),
                    },
                    {
                        label: <FormattedMessage id="requests-list-page.toolbar.popover.cancel" />,
                        onClick: () => alert("Отменить"),
                    },
                ];

                return (
                    <Box display="flex" alignItems="center" justifyContent="space-between" width="100%">
                        <Box display="flex" alignItems="center" minWidth={0}>
                            {statusInfo && statusInfo.icon}
                            <Typography ml={1} noWrap overflow="hidden" textOverflow="ellipsis">
                                {statusInfo && statusInfo.text}
                            </Typography>
                        </Box>
                        <ThreeDotsButton menuItems={menuItems} />
                    </Box>
                );
            }
            case "createdBy":
            case "updatedBy": {
                const text = row[column.id];
                const truncatedText = text.length > 20 ? `${text.slice(0, 20)}...` : text;

                return (
                    <Tooltip title={text}>
                        <Typography className="table-row-cell-text" noWrap>
                            {truncatedText}
                        </Typography>
                    </Tooltip>
                );
            }

            case "url":
                return (
                    <IconButton>
                        <LanguageIcon
                            onClick={(e) => {
                                e.stopPropagation();
                                window.open(row.url, "_blank");
                            }}
                        />
                    </IconButton>
                );
            case "taskId":
                return <Typography>{row.url?.split("/").pop()}</Typography>;
            default:
                return createRowCellContentInner(row, column, selected, onSelect, getCellValue);
        }
    };

    const {
        rows,
        columns,
        total,
        busy,
        selectedIds,
        searchString,
        setSearchString,
        pageNumber,
        setPageNumber,
        limitNumber,
        setLimitNumber,
        filterData,
        setFilterData,
        sortData,
        setSortData,
        error,
        setError,
    } = useReduxTable(
        (state: RootState) => state.administration.requests.requests,
        fetchEmployeeRequests,
        (state: RootState) => state.administration.requests.columns,
        fetchRequestsColumns,
        (state: RootState) => state.administration.requests.selectedIds,
        setSelectedIdsAction,
        (state: RootState) => state.administration.requests.searchString,
        setSearchStringAction,
        (state: RootState) => state.administration.requests.pageNumber,
        setPageNumberAction,
        (state: RootState) => state.administration.requests.limitNumber,
        setLimitNumberAction,
        (state: RootState) => state.administration.requests.sortData,
        setSortDataAction,
        (state: RootState) => state.administration.requests.filterData,
        setFilterDataAction,
        (state: RootState) => state.administration.requests.error,
        setErrorAction,
        (state: RootState) => state.administration.requests.total,
        (state: RootState) => state.administration.requests.busy
    );
    const typedError = error as ResponseError | null;
    // const [requestsType, setRequestsType] = useReduxValue((state: RootState) => state.administration.requests.requestsType, setRequestsTypeAction);
    const busyType = useSelector((state: RootState) => state.administration.requests.busyType);
    const [createRequestDialogOpen, setCreateRequestDialogOpen] = useState(false);

    const getTableRowId = (row: ChangeRequest) => row.requestId;

    const getFilters = useFilters("my-tasks");

    const toolbarActions = useMemo(
        () => [
            {
                id: "title",
                title: intl.formatMessage({
                    id: "requests-list-page.toolbar.text.title",
                }),
                type: "text",
                position: "begin",
            },
            // {
            //     id: "select",
            //     type: "select",
            //     position: "end",
            //     options: [
            //         { value: REQUESTS_TYPE.ALL, title: "Все ЗиР" },
            //         { value: REQUESTS_TYPE.USERS, title: "Мой ЗиР" },
            //     ],
            //     value: requestsType,
            //     callback: setRequestsType,
            // },
            {
                id: "add",
                title: intl.formatMessage({
                    id: "requests-list-page.toolbar.btn-create-request",
                }),
                position: "end",
                callback: () => {
                    setCreateRequestDialogOpen(true);
                },
            },
        ],
        [intl]
    );

    const handleRowClick = (row: ChangeRequest): void => {
        navigate(`/brm/${"my-tasks"}/request/${row.requestId}`);
    };

    return (
        <>
            <CommonTable
                rows={rows as object[]}
                columns={columns as object[]}
                toolbarActions={toolbarActions}
                selectedIds={selectedIds as null}
                getRowId={getTableRowId}
                pageNumber={pageNumber as number}
                setPageNumber={setPageNumber as (value: number) => void}
                limitNumber={limitNumber as number}
                setLimitNumber={setLimitNumber as (value: number) => void}
                total={total as number}
                onRowClick={handleRowClick}
                filterData={filterData as object[]}
                setFilterData={setFilterData}
                sortData={sortData as object[]}
                setSortData={setSortData as (value: object[]) => void}
                searchString={searchString as string}
                setSearchString={setSearchString as (value: string) => void}
                getCellValue={getCellValue}
                getFilters={getFilters}
                createRowCellContent={(row, column, selected, onSelect) => createRowCellContent(row, column, selected, onSelect, getCellValue, stageMap)}
                useKeyboardNavigation={false}
                toolbar={[]}
                filtersCallback={createFilterStatusPopover as () => boolean}
            />

            <ProgressDialog title={""} open={busy} busyType={busyType} progress={""} />

            <ErrorsDialog error={typedError} open={typedError?.type === "error"} onClose={() => (setError as (value: null) => void)(null)} />
            {anchorEl && <FilterStatusPopover setAnchorEl={setAnchorEl} anchorEl={anchorEl} statuses={statuses} />}
            <CreateRequestDialog
                open={createRequestDialogOpen}
                onClose={() => {
                    setCreateRequestDialogOpen(false);
                    dispatch(setValidationErrors(null));
                }}
            />
        </>
    );
};

export default TaskList;
